import React from 'react';
import {
  LoadingIndicator,
  StackLayout,
} from '@leagueplatform/web-common-components';
import { useSearchParams } from '@leagueplatform/web-common';
import { useIntl } from '@leagueplatform/locales';
import { Tabs } from '@leagueplatform/genesis-core';
import { SectionHeader } from '@leagueplatform/genesis-core-alpha';
import { CARD_STATUS, PILL_STATUS } from '@leagueplatform/wallet-api';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { NavigationSidebar } from './components/navigation-sidebar/navigation-sidebar.component';
import { NavigationGrid } from './components/navigation-grid/navigation-grid.component';
import { useNavigationSections } from './hooks/use-navigation-sections';
import { CardStatuses } from '../../utils/parse-benefit-card-statuses.util';

interface WalletItemNavigationSectionsProps {
  userId?: string;
  userRole?: string;
  layout?: 'grid' | 'sidebar';
  showHeading?: boolean;
}

export const WalletItemNavigationSections = ({
  userId,
  userRole,
  layout = 'grid',
  showHeading = false,
}: WalletItemNavigationSectionsProps) => {
  const { isLoading, itemSectioned, benefitSections, cardStatuses, heading } =
    useNavigationSections();

  const { $tmap } = useIntl();
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const tabParam = searchParams.get('tab');

  const getCardStatusMessage = $tmap<CardStatuses>({
    active: 'WALLET_BENEFIT_STATUS_ACTIVE',
    [PILL_STATUS.PENDING]: 'WALLET_BENEFIT_STATUS_PENDING',
    [PILL_STATUS.PAST]: 'WALLET_BENEFIT_STATUS_PAST',
    [CARD_STATUS.CREATED]: 'WALLET_BENEFIT_STATUS_CREATED',
    [CARD_STATUS.READY]: 'WALLET_BENEFIT_STATUS_READY',
    [CARD_STATUS.IN_WAITING_PERIOD]: 'WALLET_BENEFIT_IN_WAITING_PERIOD',
    [CARD_STATUS.INACTIVE]: 'WALLET_BENEFIT_STATUS_INACTIVE',
    [CARD_STATUS.INACTIVE_GRACE_PERIOD]:
      'WALLET_BENEFIT_STATUS_INACTIVE_GRACE_PERIOD',
    [CARD_STATUS.ALLOCATED]: 'WALLET_BENEFIT_STATUS_ALLOCATED',
    [CARD_STATUS.OPTED_OUT]: 'WALLET_BENEFIT_STATUS_OPTED_OUT',
  });

  // Handle tab change
  const handleTabChange = (value: string) => {
    // Extract the status from the tab value (remove '-tab' suffix)
    const status = value.replace('-tab', '');

    // Update URL with the selected tab
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('tab', status);

    navigate({
      search: newSearchParams.toString(),
    });
  };

  // Get the default tab value from URL or fallback to active status
  const getDefaultTabValue = () => {
    if (tabParam && cardStatuses.includes(tabParam as CardStatuses)) {
      return `${tabParam}-tab`;
    }

    const hasActiveTab =
      cardStatuses.includes(CARD_STATUS.ACTIVE) ||
      cardStatuses.includes(PILL_STATUS.ACTIVE);

    return hasActiveTab ? 'active-tab' : `${cardStatuses[0]}-tab`;
  };

  if (isLoading) {
    return <LoadingIndicator inline />;
  }

  if (layout === 'sidebar') {
    return (
      <NavigationSidebar
        userId={userId}
        userRole={userRole}
        navigationSections={itemSectioned}
      />
    );
  }

  if (cardStatuses.length === 1) {
    const singleBenefitSection = benefitSections[cardStatuses[0]];

    return (
      <StackLayout>
        {showHeading && <SectionHeader title={heading} headingLevel="2" />}
        {singleBenefitSection?.map((statuses) => (
          <React.Fragment key={statuses.section_id}>
            {statuses.sections.map((section) => (
              <NavigationGrid
                key={`${statuses.section_id}-${section.section_id}`}
                navigationSections={section}
                sectionId={section.section_id}
              />
            ))}
          </React.Fragment>
        ))}
      </StackLayout>
    );
  }

  return (
    <StackLayout>
      {showHeading && <SectionHeader title={heading} headingLevel="2" />}
      <Tabs.Root
        defaultValue={getDefaultTabValue()}
        onValueChange={handleTabChange}
      >
        <StackLayout space="$three">
          <Tabs.TabList tabDistribution="hugContents" divider>
            {cardStatuses.map((status) => (
              <Tabs.Tab value={`${status}-tab`} key={`${status}-tab`}>
                {getCardStatusMessage(status)}
              </Tabs.Tab>
            ))}
          </Tabs.TabList>

          {/* For each status create a tab-panel */}
          {cardStatuses.map((status) => (
            <Tabs.TabPanel value={`${status}-tab`} key={`${status}-tab-panel`}>
              <StackLayout>
                {benefitSections[status]?.map((statuses) => (
                  <React.Fragment key={statuses.section_id}>
                    {statuses.sections.map((section) => (
                      <NavigationGrid
                        key={`${statuses.section_id}-${section.section_id}`}
                        navigationSections={section}
                        sectionId={section.section_id}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </StackLayout>
            </Tabs.TabPanel>
          ))}
        </StackLayout>
      </Tabs.Root>
    </StackLayout>
  );
};
